import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { useCurrentStateAndParams } from "@uirouter/react";

import { AreaGraph, LegendExtension, LEGEND_POSITIONS } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { EmptyState } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { minAbbrNumberFilter } from "app/filters/ngFilters";
// eslint-disable-next-line max-len
import { LegendOthersToggle } from "app/pages/digital-marketing/keywords-campaigns/reports/overview-report/Performance-overtime/legendSubHeader/LegendOthersToggle";
import { useShareOfVoice } from "app/pages/digital-marketing/keywords-campaigns/reports/overview-report/Performance-overtime/others-context/OthersContext";
import { getVolumeValue } from "app/pages/website-analysis/traffic-sources/search/common/utils";

import ColorStack from "app/components/colorsStack/ColorStack";
import { CHART_COLORS } from "app/constants/ChartColors";
import DurationService, { BasicDurations } from "app/services/DurationService";
import { DefaultFetchService } from "app/services/fetchService";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { useMonitorKeywordsEndpoints } from "../../monitorKeywordsEndpoints";
import { ShareOfVoiceLoader, ShareOfVoiceWrapper } from "../StyledComponents";

import { getGraphConfig } from "./graphConfig";
import type { IShareOfVoiceGraphProps, IShareOfVoiceGraphData, ITransformedDataPoint } from "./types";
import { getNoDataObject, getOthersData, getOthersMonthlyAvg, transformData } from "./utils";
const QUERY_KEY = "share-of-voice-graph";

const legendValueFilter = (value: number) => minAbbrNumberFilter()(getVolumeValue(value), 50) as string;
const MAX_SELECTED_LEGEND_ITEMS = 10;

export const ShareOfVoiceGraph: FC<IShareOfVoiceGraphProps> = ({ domains, keywordGroup }) => {
    const { includeOtherShareOfVoice } = useShareOfVoice();
    const { country, webSource, duration } = useCurrentStateAndParams().params;
    const endpoints = useMonitorKeywordsEndpoints();
    const { from, to, isWindow } = DurationService.getDurationData(BasicDurations.LAST_MONTH).forAPI;
    const [colorStack] = useState<ColorStack>(new ColorStack(CHART_COLORS.chartMainColors));
    const [dataItems, setDataItems] = useState<ITransformedDataPoint[]>([]);
    const finalDuration =
        duration === BasicDurations.LAST_TWELVE_MONTHS ? BasicDurations.LAST_THIRTEEN_MONTHS : duration;
    const domainsFilter = domains?.join(",");
    const apiParams = {
        timeGranularity: GranularityNames.MONTHLY,
        From: from,
        To: to,
        keys: keywordGroup.Id,
        country,
        Device: webSource,
        Duration: finalDuration,
        isWindow,
        DomainsFilter: domainsFilter,
        sourceType: "all",
    };

    const { data, isLoading, isError } = useQuery({
        queryKey: [QUERY_KEY, domainsFilter, keywordGroup.Id, keywordGroup.GroupHash, country, webSource, duration],
        queryFn: async () =>
            DefaultFetchService.getInstance().get<IShareOfVoiceGraphData>(endpoints.shareOfVoiceGraph, apiParams),
    });

    const transformedData = useMemo(() => {
        if (!data) {
            return null;
        }
        return transformData(data, domains, colorStack);
    }, [data, domains, colorStack]);

    useEffect(() => {
        if (!isLoading) {
            setDataItems(transformedData?.graphDataItems);
        }
    }, [transformedData, isLoading]);

    const legendToggleHandler = (currentVisibleSeries: ITransformedDataPoint[]) => {
        if (!currentVisibleSeries?.length) {
            setDataItems((prevDataItems) => {
                return prevDataItems.map((item) => ({
                    ...item,
                    visible: false,
                }));
            });
        } else {
            setDataItems((prevDataItems) => {
                const updatedDataItems = prevDataItems
                    ?.filter((item) => !item.isOther)
                    .map((dataItem) => {
                        const isVisible = currentVisibleSeries?.some((visibleItem) => visibleItem.id === dataItem.id);
                        let color = dataItem.color;

                        // Only change colors if visibility is changing
                        if (isVisible !== dataItem.visible) {
                            if (isVisible) {
                                // Becoming visible - acquire new color
                                color = colorStack.acquire() as string;
                            } else {
                                // Becoming invisible - release current color
                                colorStack.release(dataItem.color);
                                color = colorsPalettes.carbon[200];
                            }
                        }

                        return {
                            ...dataItem,
                            color,
                            visible: isVisible,
                        };
                    });

                const updatedOthersData = getOthersData(transformedData.totalsData, updatedDataItems);
                const updatedOthersMonthlyAvg = getOthersMonthlyAvg(transformedData.totalsData, updatedDataItems);
                const isOthersVisible = currentVisibleSeries.some((visibleItem) => visibleItem.isOther);

                const updatedOthers = {
                    ...prevDataItems.find((item) => item.isOther),
                    data: updatedOthersData,
                    total: updatedOthersMonthlyAvg,
                    visible: isOthersVisible,
                };
                return [...updatedDataItems, updatedOthers];
            });
        }
    };

    const noDataObject = getNoDataObject(isError);

    const dataItemsToDisplay = useMemo(() => {
        return dataItems?.filter((item) => includeOtherShareOfVoice || !item?.isOther);
    }, [dataItems, includeOtherShareOfVoice]);

    if (!isLoading && !dataItems) {
        return (
            <ShareOfVoiceWrapper>
                <EmptyState {...noDataObject} />
            </ShareOfVoiceWrapper>
        );
    }

    const isToggleDisabled =
        !includeOtherShareOfVoice &&
        dataItemsToDisplay?.filter((item) => item.visible)?.length === MAX_SELECTED_LEGEND_ITEMS;

    return (
        <ShareOfVoiceWrapper>
            {isLoading || !dataItems?.length ? (
                <ShareOfVoiceLoader />
            ) : (
                <AreaGraph
                    data={dataItemsToDisplay}
                    graphConfig={getGraphConfig(dataItemsToDisplay, transformedData?.totalsData)}
                    labelsAreRotated={false}
                    withoutTooltipsPadding={false}
                    legendPosition={LEGEND_POSITIONS.RIGHT}
                    maxLegendItemsSelected={MAX_SELECTED_LEGEND_ITEMS}
                    shouldPresentValueAtLegend={true}
                    legendHeaders={[
                        translate("keywordanalysis.organic2.clicks.over.time.domain"),
                        translate("keywordanalysis.organic2.clicks.over.time.monthly.average"),
                    ]}
                    isToggleAllEnabled
                    legendGraphGap={24}
                    legendsHeight="200px"
                    legendValueFilter={legendValueFilter}
                    legendToggleHandlerCallback={legendToggleHandler}
                    keepSameLegendsOnDataUpdate={false}
                    legendsMinWidth={200}
                >
                    <LegendExtension position="before">
                        <LegendOthersToggle
                            isDisabled={isToggleDisabled}
                            tooltip={{
                                enabled: isToggleDisabled,
                                content: translate("monitor.keyword.page.share.of.voice.others.toggle.tooltip"),
                            }}
                        />
                    </LegendExtension>
                </AreaGraph>
            )}
        </ShareOfVoiceWrapper>
    );
};
